
import React, { useState } from 'react';
import { Lock, User, ArrowRight, AlertCircle, Info, Loader2 } from 'lucide-react';
import { login } from '../services/api/auth';

interface AdminLoginProps {
    onLogin: () => void;
    onCancel: () => void;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin, onCancel }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(email, password);
            onLogin();
        } catch (err: any) {
            setError(err?.message || 'Email atau Password salah!');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 animate-fadeIn">
            <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-md">
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Lock className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Admin Portal</h2>
                    <p className="text-gray-500 text-sm">Kelola data pengunjung GIZ & RIW</p>
                </div>

                {/* Helper Credential Info */}
                <div className="mb-6 bg-blue-50 border border-blue-100 p-4 rounded-2xl flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                    <div>
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Login dengan Akun Backend</p>
                        <p className="text-xs text-blue-700 font-medium">
                            Gunakan email dan password yang terdaftar di database backend.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Email</label>
                        <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none transition-all"
                                placeholder="Masukkan Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="password"
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-giz-primary outline-none transition-all"
                                placeholder="Masukkan Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg border border-red-100 animate-slideUp">
                            <AlertCircle className="w-4 h-4" /> {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading || !email || !password}
                        className="w-full bg-gray-900 text-white font-black uppercase tracking-widest text-xs py-5 rounded-2xl shadow-lg mt-4 hover:bg-black transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Memproses...
                            </>
                        ) : (
                            'Masuk Dashboard'
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={onCancel}
                        className="w-full py-3 text-gray-400 text-xs font-black uppercase tracking-widest hover:text-gray-600 transition-colors"
                    >
                        Kembali Ke Landing Page
                    </button>
                </form>
            </div>

            <p className="mt-8 text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em]">
                GIZ & RIW Management System &copy; 2025
            </p>
        </div>
    );
};
